/**
 * Game-specific user profile (skills and custom properties per game).
 * User doc can have:
 *   - Top-level: skillLevel, yearsOfExperience (legacy / fallback)
 *   - gameProfiles: { [gameId]: { skillLevel, yearsOfExperience, ...custom } }
 */

import { DEFAULT_GAME_ID } from '../constants/games';

/**
 * Get merged profile for a game: gameProfiles[gameId] with fallback to top-level.
 * @param {object} profile - User profile (user doc or userProfile from Auth)
 * @param {string} [gameId] - Game id (defaults to DEFAULT_GAME_ID)
 * @returns {{ skillLevel: string, yearsOfExperience: number, [key: string]: unknown }}
 */
export function getProfileForGame(profile, gameId = DEFAULT_GAME_ID) {
  if (!profile) {
    return { skillLevel: 'average', yearsOfExperience: 0 };
  }
  const gameData = (profile.gameProfiles && profile.gameProfiles[gameId]) || {};
  return {
    skillLevel: gameData.skillLevel ?? profile.skillLevel ?? 'average',
    yearsOfExperience:
      gameData.yearsOfExperience != null
        ? Number(gameData.yearsOfExperience)
        : (profile.yearsOfExperience != null ? Number(profile.yearsOfExperience) : 0),
    ...gameData,
  };
}

/**
 * Build gameProfiles update for a game (for use with updateDoc).
 * Does not remove other games' data.
 * @param {string} gameId
 * @param {{ skillLevel?: string, yearsOfExperience?: number, [key: string]: unknown }} data
 * @returns {object} - Partial user doc: { gameProfiles: { ... } }
 */
export function gameProfileUpdatePayload(gameId, data) {
  if (!gameId || !data) return {};
  const slice = {
    ...(data.skillLevel != null && { skillLevel: data.skillLevel }),
    ...(data.yearsOfExperience != null && { yearsOfExperience: data.yearsOfExperience }),
  };
  const customKeys = Object.keys(data).filter(
    (k) => k !== 'skillLevel' && k !== 'yearsOfExperience'
  );
  customKeys.forEach((k) => {
    slice[k] = data[k];
  });
  return {
    gameProfiles: {
      [gameId]: slice,
    },
  };
}
